import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Field, Btn, inputCls } from "../components/common";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";
import { Camera, Trash2, Plus, PenTool } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [specimenUrl, setSpecimenUrl] = useState(null);
  const [pw, setPw] = useState({ old_password: "", new_password: "" });
  const [showPad, setShowPad] = useState(false);
  const photoRef = useRef(null);
  const specRef = useRef(null);

  const load = () => {
    api.get("/profile/me").then((r) => {
      const d = r.data;
      setP({
        phone_wa: d.phone_wa || "", alamat: d.alamat || "",
        identitas: d.identitas || {}, pasangan: d.pasangan || {}, anak: d.anak || [], rekening_gaji: d.rekening_gaji || {},
        jabatan: d.jabatan || "", role: d.role, name: d.name, email: d.email,
      });
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/profile/me/photo", { responseType: "blob" }).then((r) => setPhotoUrl(URL.createObjectURL(r.data))).catch(() => setPhotoUrl(null));
    api.get("/users/me/signature", { responseType: "blob" }).then((r) => setSpecimenUrl(URL.createObjectURL(r.data))).catch(() => setSpecimenUrl(null));
  };
  useEffect(load, []);

  if (!p) return <div className="text-slate-500" data-testid="profile-loading">Memuat profil...</div>;

  const save = async () => {
    try {
      const ident = { ...p.identitas };
      if (ident.tempat_lahir && ident.tanggal_lahir) ident.ttl = `${ident.tempat_lahir}, ${ident.tanggal_lahir.split("-").reverse().join("-")}`;
      await api.put("/profile/me", { phone_wa: p.phone_wa, alamat: p.alamat, identitas: ident, pasangan: p.pasangan, anak: p.anak, rekening_gaji: p.rekening_gaji });
      toast.success("Profil berhasil disimpan");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setI = (k, v) => setP({ ...p, identitas: { ...p.identitas, [k]: v } });
  const setPs = (k, v) => setP({ ...p, pasangan: { ...p.pasangan, [k]: v } });
  const setR = (k, v) => setP({ ...p, rekening_gaji: { ...p.rekening_gaji, [k]: v } });
  const setAnak = (i, k, v) => { const a = [...p.anak]; a[i] = { ...a[i], [k]: v }; setP({ ...p, anak: a }); };

  const uploadPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 640 / img.width);
        canvas.width = img.width * scale; canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        try {
          const r = await api.post("/profile/me/photo", { photo: canvas.toDataURL("image/jpeg", 0.85) });
          toast.success(r.data.message);
          load();
        } catch (err) { toast.error(errMsg(err)); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const uploadSpecimen = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/users/me/signature", { signature: reader.result });
        toast.success("Specimen tanda tangan tersimpan");
        load();
      } catch (err) { toast.error(errMsg(err)); }
    };
    reader.readAsDataURL(file);
  };

  const changePw = async () => {
    try {
      const r = await api.put("/profile/me/password", pw);
      toast.success(r.data.message);
      setPw({ old_password: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-3xl" data-testid="profile-page">
      <PageHeader title="Profil Saya" subtitle="Lengkapi data diri — foto profil dipakai untuk verifikasi wajah absensi" testid="profile-header" />

      <Card className="mb-4" testid="profile-identity">
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="flex flex-col items-center gap-2">
            {photoUrl ? (
              <img src={photoUrl} alt="foto profil" className="w-28 h-28 rounded-2xl object-cover border border-slate-700" data-testid="profile-photo" />
            ) : (
              <div className="w-28 h-28 rounded-2xl bg-slate-800 border border-dashed border-slate-600 flex items-center justify-center text-slate-500"><Camera className="w-6 h-6" /></div>
            )}
            <input ref={photoRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="profile-photo-input" />
            <Btn tone="ghost" className="!text-xs" onClick={() => photoRef.current.click()} data-testid="profile-photo-button">{photoUrl ? "Ganti Foto" : "Unggah Foto Profil"}</Btn>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nama"><input className={inputCls} value={p.name} disabled /></Field>
            <Field label="Jabatan (diisi admin)"><input className={inputCls} value={p.jabatan || p.role} disabled data-testid="profile-jabatan" /></Field>
            <Field label="NIK"><input className={inputCls} value={p.identitas.nik || ""} onChange={(e) => setI("nik", e.target.value)} data-testid="profile-nik" /></Field>
            <Field label="Tempat Lahir"><input className={inputCls} value={p.identitas.tempat_lahir || ""} onChange={(e) => setI("tempat_lahir", e.target.value)} placeholder="Stabat" data-testid="profile-tempat-lahir" /></Field>
            <Field label="Tanggal Lahir"><input type="date" className={inputCls} value={p.identitas.tanggal_lahir || ""} onChange={(e) => setI("tanggal_lahir", e.target.value)} data-testid="profile-tanggal-lahir" /></Field>
            <Field label="No. WhatsApp"><input className={inputCls} value={p.phone_wa} onChange={(e) => setP({ ...p, phone_wa: e.target.value })} data-testid="profile-wa" /></Field>
            <Field label="Alamat Rumah"><input className={inputCls} value={p.alamat} onChange={(e) => setP({ ...p, alamat: e.target.value })} data-testid="profile-alamat" /></Field>
          </div>
        </div>
      </Card>

      <Card className="mb-4" testid="profile-family">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Identitas Pasangan & Anak</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <Field label="Nama Pasangan"><input className={inputCls} value={p.pasangan.nama || ""} onChange={(e) => setPs("nama", e.target.value)} data-testid="profile-spouse" /></Field>
          <Field label="NIK Pasangan"><input className={inputCls} value={p.pasangan.nik || ""} onChange={(e) => setPs("nik", e.target.value)} /></Field>
          <Field label="TTL Pasangan"><input className={inputCls} value={p.pasangan.ttl || ""} onChange={(e) => setPs("ttl", e.target.value)} /></Field>
        </div>
        {p.anak.map((a, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 mb-2">
            <Field label={`Nama Anak ${i + 1}`}><input className={inputCls} value={a.nama || ""} onChange={(e) => setAnak(i, "nama", e.target.value)} data-testid={`profile-child-${i}`} /></Field>
            <Field label="TTL Anak"><input className={inputCls} value={a.ttl || ""} onChange={(e) => setAnak(i, "ttl", e.target.value)} /></Field>
            <div className="flex items-end"><Btn tone="danger" className="!px-2.5 !py-1.5 !text-xs" onClick={() => setP({ ...p, anak: p.anak.filter((_, x) => x !== i) })} data-testid={`profile-child-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Btn></div>
          </div>
        ))}
        <Btn tone="ghost" className="!text-xs" onClick={() => setP({ ...p, anak: [...p.anak, { nama: "", ttl: "" }] })} data-testid="profile-child-add"><Plus className="w-3.5 h-3.5" /> Tambah Anak</Btn>
      </Card>

      <Card className="mb-4" testid="profile-bank">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Rekening Gaji</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Bank"><input className={inputCls} value={p.rekening_gaji.bank || ""} onChange={(e) => setR("bank", e.target.value)} placeholder="BTN / BSI / BRI..." data-testid="profile-bank" /></Field>
          <Field label="No. Rekening"><input className={inputCls} value={p.rekening_gaji.no || ""} onChange={(e) => setR("no", e.target.value)} data-testid="profile-rek" /></Field>
          <Field label="Atas Nama"><input className={inputCls} value={p.rekening_gaji.atas_nama || ""} onChange={(e) => setR("atas_nama", e.target.value)} /></Field>
        </div>
        <div className="flex justify-end mt-3"><Btn onClick={save} data-testid="profile-save">Simpan Profil</Btn></div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card testid="profile-specimen">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Specimen Tanda Tangan</div>
          {specimenUrl ? (
            <div className="bg-white border border-slate-700 rounded-lg p-3 flex justify-center mb-2"><img src={specimenUrl} alt="specimen" className="max-h-20" data-testid="profile-specimen-img" /></div>
          ) : <p className="text-xs text-slate-500 mb-2">Belum ada specimen.</p>}
          {showPad && <SignaturePad onSave={async (sig) => { await api.post("/users/me/signature", { signature: sig }); toast.success("Specimen tersimpan"); setShowPad(false); load(); }} />}
          <div className="flex gap-2 mt-2">
            <Btn tone="ghost" className="!text-xs" onClick={() => setShowPad(!showPad)} data-testid="profile-specimen-pad"><PenTool className="w-3.5 h-3.5" /> {showPad ? "Tutup Pad" : "Tanda Tangan di Layar"}</Btn>
            <input ref={specRef} type="file" accept="image/png" onChange={uploadSpecimen} className="hidden" />
            <Btn tone="ghost" className="!text-xs" onClick={() => specRef.current.click()} data-testid="profile-specimen-upload">Unggah PNG</Btn>
          </div>
        </Card>
        <Card testid="profile-password">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Ubah Password</div>
          <div className="space-y-3">
            <Field label="Password Lama"><input type="password" className={inputCls} value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} data-testid="profile-old-pw" /></Field>
            <Field label="Password Baru (min 6 karakter)"><input type="password" className={inputCls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="profile-new-pw" /></Field>
            <div className="flex justify-end"><Btn onClick={changePw} disabled={!pw.old_password || !pw.new_password} data-testid="profile-pw-save">Ubah Password</Btn></div>
          </div>
        </Card>
      </div>
    </div>
  );
}
